import { CheckCircleOutlined, MinusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  TimePicker
} from "antd";
import PhoneInput from "react-phone-input-2";
import moment from "moment";
import _isEmpty from "lodash/isEmpty";

import AddressInput from "components/inputs/AddressInput";
import DefaultMapbox from "modules/client/active-orders/components/mapbox/DefaultMapbox";
import React, { useState } from "react";
import { ReactSVG } from "react-svg";
import "./ReturnProcess.scss";
import { TIME_FORMAT_BACKEND } from "configs/constants";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectRole } from "modules/auth/store/authSlice";
import { allowPermission } from "helper/util";
import { canCreatePermission } from "configs/constants";
import { canEditPermission } from "configs/constants";

const ReturnProcess = ({ returnLocation, isLoading, onSubmit, profile }) => {
  const [currentAddress, setCurrentAddress] = useState();
  const [returnLocationTouched, setReturnLocationTouched] = useState(false);
  const [form] = Form.useForm();
  const role = useSelector(selectRole);
  const canCreate = allowPermission(role, canCreatePermission);
  const canEdit = allowPermission(role, canEditPermission);

  useEffect(() => {
    const { start, end } = returnLocation;
    const isUpdating = !!returnLocation?.id;
    form.setFieldsValue({
      ...returnLocation,
      start: start && isUpdating ? moment(start, TIME_FORMAT_BACKEND) : start,
      end: end && isUpdating ? moment(end, TIME_FORMAT_BACKEND) : end
    });
    if (isUpdating) {
      setCurrentAddress({
        address: returnLocation.address,
        lngLat: [returnLocation.lng, returnLocation.lat]
      });
      // If return location already exists, mark as touched so validation is enforced
      setReturnLocationTouched(true);
    }
  }, [form, returnLocation]);

  useEffect(() => {
    if (!_isEmpty(profile)) {
      form.setFieldsValue({
        preferredNoOfStops: profile.client?.preferredNoOfStops
      });
    }
  }, [form, profile]);

  const handleSetAddress = (value) => {
    setCurrentAddress({ address: value.address, lngLat: value.lngLat });
    setReturnLocationTouched(true);
  };

  const handleFieldChange = () => {
    setReturnLocationTouched(true);
  };

  const handleOnFinish = async (values) => {
    const { address, lngLat } = currentAddress || {};
    const { start, end } = values;

    // If return location fields have been touched, validate required fields
    if (returnLocationTouched) {
      const errors = [];

      if (!address || !lngLat) {
        errors.push({ name: "address", errors: ["Address is required"] });
      }
      if (!start) {
        errors.push({ name: "start", errors: ["Start time is required"] });
      }
      if (!end) {
        errors.push({ name: "end", errors: ["End time is required"] });
      }
      if (!values.duration) {
        errors.push({ name: "duration", errors: ["Duration is required"] });
      }
      if (!values.contactName) {
        errors.push({ name: "contactName", errors: ["Contact name is required"] });
      }

      if (errors.length > 0) {
        form.setFields(errors);
        return;
      }
    }

    onSubmit({
      ...values,
      address,
      lng: lngLat?.[0],
      lat: lngLat?.[1],
      start: start
        ? typeof start === "object"
          ? start.format(TIME_FORMAT_BACKEND)
          : moment(start).format(TIME_FORMAT_BACKEND)
        : undefined,
      end: end
        ? typeof end === "object"
          ? end.format(TIME_FORMAT_BACKEND)
          : moment(end).format(TIME_FORMAT_BACKEND)
        : undefined,
      enabledReturnPickup: values?.enabledReturnPickup ?? false,
      enabled: values?.enabled ?? false,
      preferredNoOfStops: values?.preferredNoOfStops
    });
  };

  return (
    <div className="common-form return-process-form">
      <Form
        scrollToFirstError={true}
        layout="vertical"
        hideRequiredMark
        colon={false}
        onFinish={handleOnFinish}
        form={form}
      >
        <h2 className="title">Route Options</h2>

        <h2 className="form-name">ROUTING PROCESS</h2>
        <div className="form">
          <Row gutter={24}>
            <Col span={12}>
              <h4>Stops per Route</h4>
              <div className="desc" style={{ fontSize: 13 }}>
                Sets the number of stops routes are divided into if total of stops is
                higher
              </div>
              <Form.Item
                name="preferredNoOfStops"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  min={2}
                  max={99}
                  size="large"
                  placeholder="Stops per Route"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <h2 className="title" style={{ marginTop: 32 }}>
          Automated Return Pickup Location Process{" "}
          <div className="switcher-btn">
            <Form.Item valuePropName="checked" name="enabledReturnPickup">
              <Switch />
            </Form.Item>
            <span> &nbsp;Enabled</span>
          </div>
        </h2>
        <h3 className="title-desc">
          If enabled, at creation of a Job it will automatically add a Return Pickup stop
          to the Pickup Location including all packages. The drive from the last stop back
          to the pickup is billed as return distance / return time on the client and
          driver estimates. This is the default for new orders; it can be changed per
          order on the Plan Your Delivery Routes page.
        </h3>
        <h2 className="title">
          Automated Return Process for Failed Stops or Partial Completions{" "}
          <div className="switcher-btn">
            <Form.Item valuePropName="checked" name="enabled">
              <Switch />
            </Form.Item>
            <span> &nbsp;Enabled</span>
          </div>
        </h2>
        <h3 className="title-desc">
          If enabled, at completion of a Job it will automatically add a Return stop to
          the Return Location including all packages not delivered. This is the default
          for new orders; it can be changed per order on the Plan Your Delivery Routes
          page.
        </h3>
        <h2 className="form-name">RETURN LOCATION</h2>
        <div className="form">
          <Form.Item label="Address (move pin to exact location)" name="address">
            <AddressInput
              placeholder="Address"
              onSelect={(value) => handleSetAddress(value)}
              address={currentAddress}
            />
          </Form.Item>
          <div className="map">
            <DefaultMapbox address={currentAddress} setAddress={handleSetAddress} />
          </div>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%"
                    }}
                  >
                    <div className="left">Location Name</div>
                    <div className="right" style={{ fontWeight: 400, color: "#888888" }}>
                      Optional
                    </div>
                  </div>
                }
                name="locationName"
              >
                <Input placeholder="Location Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%"
                    }}
                  >
                    <div className="left">Physical Address</div>
                    <div className="right" style={{ fontWeight: 400, color: "#888888" }}>
                      Optional
                    </div>
                  </div>
                }
                name="physicalAddress"
              >
                <Input placeholder="Physical Address" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <div className="custom-label">
                <label>Pickup Time Window</label>
              </div>
              <Row className="from_to">
                <Col span={11}>
                  <Form.Item name="start">
                    <TimePicker
                      format={TIME_FORMAT_BACKEND}
                      value={form.getFieldValue("start")}
                      onSelect={(value) => {
                        form.setFieldsValue({ start: value });
                        handleFieldChange();
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={2} style={{ textAlign: "center" }}>
                  <MinusOutlined />
                </Col>
                <Col span={11}>
                  <Form.Item name="end">
                    <TimePicker
                      format={TIME_FORMAT_BACKEND}
                      value={form.getFieldValue("end")}
                      onSelect={(value) => {
                        form.setFieldsValue({ end: value });
                        handleFieldChange();
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Est. Waiting/Service Time"
                name="duration"
                tooltip={{
                  title:
                    "Please assign an estimated duration in minutes of time taken from when the driver arrives at the location, is attended, finishes the delivery and resumes driving. Here it is better to go with a worst case scenario to ensure the routes can be finished."
                }}
              >
                <Input
                  type="number"
                  placeholder="Est. Waiting/Service Time"
                  suffix="Minutes"
                  onChange={handleFieldChange}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Note" name="note">
            <Input.TextArea placeholder="Note" />
          </Form.Item>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Contact Name" name="contactName">
                <Input placeholder="Contact Name" onChange={handleFieldChange} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%"
                    }}
                  >
                    <div className="left">E-mail</div>
                    <div className="right" style={{ fontWeight: 400, color: "#888888" }}>
                      Optional
                    </div>
                  </div>
                }
                name="email"
                rules={[{ type: "email", message: "Invalid Email" }]}
              >
                <Input placeholder="E-mail" />
              </Form.Item>
            </Col>
          </Row>
          <Col span={12}>
            <div className="custom-label">
              <label>Phone Numbers</label>
            </div>
            <Form.List name="phoneList" label="Phone Numbers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKeyValue, ...restField }, index) => {
                    return (
                      <div key={name} className={`phone_numbers x${key}`}>
                        <Row gutter={24}>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, "type"]}
                              fieldKey={[fieldKeyValue, "type"]}
                            >
                              <Select>
                                <Select.Option value="contact">Contact</Select.Option>
                                <Select.Option value="salesperson">
                                  Salesperson
                                </Select.Option>
                                <Select.Option value="mainSupport">
                                  Main Support
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={16}>
                            <Row>
                              <Col span={index > 0 ? 19 : 24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "phone"]}
                                  fieldKey={[fieldKeyValue, "phone"]}
                                >
                                  <PhoneInput country="ca" enableSearch />
                                </Form.Item>
                              </Col>
                              {index > 0 && (
                                <Col span={5} className="icon">
                                  <ReactSVG
                                    src="/icons/minus.svg"
                                    onClick={() => remove(name)}
                                  />
                                </Col>
                              )}
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    );
                  })}
                  <Button
                    className="add-more-contact-btn"
                    type="link"
                    onClick={() => add({ type: "contact" })}
                  >
                    Add More Phone Number
                    <ReactSVG src="/icons/plus.svg" className="react-icon" />
                  </Button>
                </>
              )}
            </Form.List>
          </Col>
        </div>
        <div className="form-footer">
          <Button loading={isLoading} type="primary" htmlType="submit">
            Update
            <CheckCircleOutlined />
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ReturnProcess;
